<script setup lang="ts">
import TeamModal from '../TeamModal/TeamModal.vue'
import TeamMember from '../TeamMember/TeamMember.vue'
import type { IEmits, IProps } from './TeamMembers.types'

const { t } = useI18n()

let memberIndex = ref<number>(-1)
const emit = defineEmits<IEmits>()

withDefaults(defineProps<IProps>(), {
  showMore: 'modal',
})

const localePath = useLocalePath()

const memberRoute = (slug:string) => {
  return localePath({
    name: 'for-investors-corporate-governance-management-board-member',
    params: { member: slug },
  })
}

</script>

<template>
  <div class="Team-Members">
    <div class="Team-Members__header">
      <UiHeader
        :header="boardTitle"
        position="center"
      />
    </div>

    <p
      v-if="boardLead"
      class="Team-Members__lead"
    >
      {{ boardLead }}
    </p>

    <div class="Team-Members__membersContainer">
      <div
        v-for="(member, index) in boardMembers"
        :key="member.boardMemberName"
        class="Team-Members__singleMember"
      >
        <TeamMember
          :board-member-photo="member?.boardMemberPhoto"
          :board-member-name="member.boardMemberName"
          :board-member-role="member.boardMemberRole"
          :learn-more-aria-label="`${t('common.learnMoreAbout')} ${member.boardMemberName}`"
        />
        <button
          v-if="showMore === 'modal'"
          class="Team-Members__showModal"
          @click="memberIndex = index"
        >
          <p
            class="Team-Members__showModalText"
            :aria-label="`${t('common.learnMoreAbout')} ${member.boardMemberName}`"
          >
            {{ $t('common.more') }}
          </p>

          <div class="Team-Members__showModalArrow">
            <img src="/icons/icn_arrow_right.svg">
          </div>
        </button>

        <NuxtLink
          v-else-if="showMore === 'uri' && member.boardMemberSlug"
          :to="memberRoute(member.boardMemberSlug)"
          class="Team-Members__showLink"
        >
          <p
            class="Team-Members__showModalText"
            :aria-label="`${t('common.learnMoreAbout')} ${member.boardMemberName}`"
          >
            {{ $t('common.more') }}
          </p>

          <div class="Team-Members__showModalArrow">
            <img src="/icons/icn_arrow_right.svg">
          </div>
        </NuxtLink>
      </div>
    </div>

    <TeamModal
      v-if="(showMore === 'modal') && (memberIndex > -1)"
      :id="modalAriaLabel"
      :model-value="memberIndex"
      :all-board-members="boardMembers"
      @update:model-value="(val) => {
        emit('update:modelValue', val)
        memberIndex = val
      }"
    />
  </div>
</template>

<style scoped lang="scss">
.Team-Members {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 1.6rem;

  &__header {
    margin-bottom: 2.4rem
  }

  &__lead {
    @include body-1-medium;
    max-width: 56rem;
    margin-bottom: 2.9rem;
    text-align: center;
  }

  &__membersContainer {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    width: 100%;
  }

  &__singleMember {
    height: 30rem;
    margin-bottom: 4.8rem;
  }

  &__showModal {
    display: flex;
    margin: 2.4rem auto 0 auto;
    cursor: pointer;
  }

  &__showModalText {
    @include body-2-medium;
    margin-right: 0.8rem;
    color: $text-400;
  }

  &__showModalArrow {
    font-size: 2rem;
  }

  &__showLink {
    display: flex;
    justify-content: center;
    margin: 2.4rem auto 0 auto;
    cursor: pointer;
  }
}

@include atM {
  .Team-Members {
    max-width: 136rem;
    margin: 6rem auto;
    padding: 0;

    &__header {
      margin-bottom: 3.2rem
    }

    &__lead {
      @include body-1-medium;
      max-width: 56rem;
      margin-bottom: 7.7rem;
    }

    &__membersContainer {
      justify-content: space-around;
      max-width: 109rem;
    }

    &__singleMember {
      height: 40rem;
      margin-bottom: 6rem;
    }

    &__showModal {
      @include body-2-medium;
      margin-bottom: 5.7rem;
    }

    &__showLink {
      @include body-2-medium;
      margin-bottom: 5.7rem;
    }
  }
}
</style>
